import React from 'react'
import styled from 'styled-components'
import { styleTheme } from '../../style/theme'
import { format, isSameDay, addDays } from 'date-fns'

import { BridalFairsContainerProps, BridalFairsProps } from './BridalFairs.types'
import { getPrimaryColor } from '../../style/utils'
import { useConfig } from '../../routes'
import { useSearch } from './hooks/useSearch'
import { usePickupBridalFairParticularDetails } from './hooks/usePickupedBridalFairParticularDetails'
import { useParams } from 'react-router-dom'
import { CalendarForm } from './components/CalendarForm'
import { FairEventListOnDate } from './components/FairEventListOnDate'
import { RecommendedBridalFairs } from './components/PickupParticularFairs'
import { useJpHolidays } from '../../hooks/useJpHolidays'
import { useDates } from './hooks/useDates'

type ConfigType = {
  primaryColor: string
  viewTitle?: string | null
}

export const BridalFairs: React.FC<BridalFairsContainerProps> = (props) => {
  const config = useConfig() as ConfigType
  return <StyledPage {...props} primaryColor={config.primaryColor} viewTitle={config.viewTitle} />
}

const Page: React.FC<BridalFairsProps> = ({ className, primaryColor, viewTitle }) => {
  const { bridalFairPageKey } = useParams() as { bridalFairPageKey: string }
  const { dates, initialDate } = useDates({ pageKey: bridalFairPageKey })
  const { bridalFairDates, selectedDate, onChange } = useSearch({ pageKey: bridalFairPageKey, initialDate })
  const holidays = useJpHolidays(addDays(new Date(), 1), addDays(new Date(), 90))
  const { pickupParticularFairs } = usePickupBridalFairParticularDetails(bridalFairPageKey, {
    per: 2,
    filter: { startDate: addDays(new Date(), 1), endDate: addDays(new Date(), 15) },
  })

  return (
    <div className={className} id="targetTop">
      {viewTitle && <h3 className="title">{viewTitle}</h3>}
      <div className="recommended-fair-wrapper">
        {pickupParticularFairs?.map((pickupParticularFair) => (
          <div key={pickupParticularFair.id}>
            <RecommendedBridalFairs
              pickupParticularFair={pickupParticularFair}
              primaryColor={primaryColor}
              holidays={holidays}
              bridalFairPageKey={bridalFairPageKey}
            />
          </div>
        ))}
      </div>
      <div>
        <CalendarForm selectedDate={selectedDate} onChange={onChange} includeDates={dates ? dates : []} />
      </div>
      {/* <p>{`検索結果${selectedDate ? `: ${format(selectedDate, 'yyyy年MM月dd日')}` : ''}`}</p> */}
      <div className="result-wrapper">
        {selectedDate ? <span>{'検索結果 : '}</span> : <span>{'検索結果'}</span>}
        {selectedDate && <span>{format(selectedDate, 'yyyy年MM月dd日')}</span>}
      </div>
      {selectedDate ? (
        <div className="events-wrapper">
          {bridalFairDates.map((bridalFairDate, index) => (
            <FairEventListOnDate
              key={`bridalFairDate.${format(bridalFairDate.date, 'yyyy-MM-dd')}.${index}`}
              bridalFairDate={bridalFairDate}
              isHoliday={holidays.some((holiday) => isSameDay(holiday.date, bridalFairDate.date))}
              bridalFairPageKey={bridalFairPageKey}
            />
          ))}
        </div>
      ) : (
        <span>日付を選択してください</span>
      )}
    </div>
  )
}

const StyledPage = styled(Page)`
  display: grid;
  gap: 24px;
  max-width: 960px;
  margin: 0 auto;
  background-color: ${styleTheme.colors.white};
  padding: 32px 0;
  @media (min-width: 769px) {
    padding: 32px 16px;
  }

  > .title {
    text-align: center;
    color: ${getPrimaryColor};
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 16px;
  }

  > .recommended-fair-wrapper {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding: 4px;

    > div {
      width: 100%;
    }

    @media (min-width: 769px) {
      padding: 0;

      > div {
        width: calc(50% - 4px);
      }
    }
  }

  > .events-wrapper {
    display: grid;
    gap: 24px;
  }

  > span {
    color: ${styleTheme.colors.black};
    line-height: 1.5;
    font-size: 16px;
    font-weight: 400;
    @media screen and (max-width: 520px) {
      margin-left: 16px;
    }
  }

  > .result-wrapper {
    display: flex;
    gap: 8px;
    height: 32px;
    color: ${getPrimaryColor};
    font-weight: 700;
    font-size: 16px;
    vertical-align: middle;
    @media screen and (max-width: 520px) {
      margin-left: 16px;
    }
    > span {
      display: grid;
      place-content: center;
      &:nth-child(2) {
        font-size: 24px;
      }
    }
  }
`
